import { ViewCode } from "@edu/shared";

/**
 * Просмотр кабинета преподавателя админом, только на чтение (раздел 12
 * `docs/admin-panel-design.md`). Чистые правила без React, чтобы их можно было
 * проверить встроенным `node:test`.
 */

/** Адрес, на который панель выписывает ссылку: `/view#<код>`. */
export const VIEW_PATH = "/view";

/** Текст общего отказа `view_only`: один на всё приложение. */
export const VIEW_ONLY_TEXT = "Только просмотр, изменения не сохраняются";

/**
 * Код из фрагмента адреса, либо `null`, если там не код.
 *
 * Проверяется той же схемой, что и на сервере: мусор во фрагменте это не повод
 * ходить в сеть, отказ всё равно был бы тем же.
 */
export function readViewCode(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const parsed = ViewCode.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Показывать ли общее «Только просмотр, изменения не сохраняются» на отказ
 * `view_only` по этому пути.
 *
 * ⚠️ Отказ запросу, который человек НЕ делал руками, молчит. Телеметрия,
 * отметки «прочитано», «Что нового просмотрено» и токен видео уходят сами при
 * открытии экрана, и полоса вспыхивала бы на каждом переходе. Писать на эти
 * запросы нечего: сервер их отклоняет, и так и задумано (12.3 проекта).
 */
export function viewOnlyNoticeFor(pathname: string): boolean {
  if (pathname.startsWith("/v1/telemetry")) return false;
  const last = pathname.split("/").filter((s) => s.length > 0).at(-1) ?? "";
  return !QUIET_TAILS.has(last);
}

/** Последние сегменты фоновых записей, см. `viewOnlyNoticeFor`. */
const QUIET_TAILS: ReadonlySet<string> = new Set(["read", "changelog-seen", "token", "viewed"]);

/**
 * Можно ли убирать локальные базы досок по только что полученному списку уроков.
 *
 * `fresh` это ответ `getMe`, взятый ПОСЛЕ списка, `ownerId` это кто в сессии по
 * мнению вкладки. ⚠️ Состояние вкладки не знает, что соседняя вкладка того же
 * браузера заменила cookie просмотром: тогда список чужой, и уборка по нему
 * стёрла бы базы собственных уроков админа, включая несинхронизированные.
 */
export function mayPruneBoards(
  fresh: { userId: string; viewer?: unknown },
  ownerId: string | null,
): boolean {
  if (ownerId === null) return false;
  if (fresh.viewer !== undefined && fresh.viewer !== null) return false;
  return fresh.userId === ownerId;
}

/**
 * Канал «сессия в этом браузере сменилась»: просмотр начат или закончен.
 *
 * Cookie одна на браузер, и вкладка кабинета, открытая до просмотра, иначе
 * жила бы со старым состоянием: писала бы доску в свою локальную базу, а полоса
 * во вкладке первого просмотра называла бы прежнее имя. Получив сообщение,
 * вкладка перезагружается.
 */
export const SESSION_CHANGED_CHANNEL = "edu.session-changed";

/** Метка этой вкладки: отличить свою рассылку от чужой. */
const TAB_ID = Math.random().toString(36).slice(2);

/**
 * Кто теперь в сессии браузера, строкой для вести соседним вкладкам: `userId`,
 * а у просмотра кабинета `userId:view`. Просмотр это та же личность, но чужая
 * cookie, и вкладка кабинета того же человека обязана о ней узнать.
 */
export function sessionIdentity(me: { userId: string; viewer?: unknown } | null): string | null {
  if (me === null) return null;
  return me.viewer !== undefined && me.viewer !== null ? `${me.userId}:view` : me.userId;
}

/**
 * Разослать соседним вкладкам «сессия сменилась» вместе с тем, кто теперь в
 * ней (`sessionIdentity`), либо `null` при выходе. Решает ПРИНИМАЮЩАЯ вкладка:
 * вход того же человека в соседней вкладке не перезагружает вкладку с уроком.
 * `BroadcastChannel`, а где его нет, событие `storage` (оно приходит всем
 * вкладкам, кроме пишущей).
 */
export function announceSessionChanged(identity: string | null): void {
  const stamp = `${TAB_ID}:${String(Date.now())}:${identity ?? ""}`;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const ch = new BroadcastChannel(SESSION_CHANGED_CHANNEL);
      ch.postMessage(stamp);
      ch.close();
      return;
    }
    localStorage.setItem(SESSION_CHANGED_CHANNEL, stamp);
  } catch {
    /* хранилище не пишет: соседние вкладки узнают о смене на следующем 401 */
  }
}

/**
 * Разбор вести: чья вкладка и кто теперь в сессии; `undefined` у мусора.
 * Весть старого бандла третьего поля не несёт: тогда `identity` это
 * `undefined`, «неизвестно кто», и вкладка перезагружается, как раньше.
 */
export function parseSessionStamp(
  stamp: unknown,
): { tab: string; identity: string | null | undefined } | undefined {
  if (typeof stamp !== "string") return undefined;
  const m = /^([^:]+):(\d+)(?::(.*))?$/su.exec(stamp);
  if (m === null) return undefined;
  const third = m[3];
  return { tab: m[1] as string, identity: third === undefined ? undefined : third === "" ? null : third };
}

/**
 * Перезагружаться ли вкладке, получившей весть. Да, если в сессии теперь не
 * тот, кто у неё (вход другого, выход, вход в просмотр и выход из него), и
 * всегда, если весть не говорит, кто (старый бандл). Вкладке без сессии от
 * чужого выхода менять нечего.
 */
export function reloadOnSessionChange(own: string | null, incoming: string | null | undefined): boolean {
  return incoming === undefined || incoming !== own;
}

/** Подписка на «сессия сменилась» из соседних вкладок. Возвращает отписку. */
export function onSessionChanged(fn: (identity: string | null | undefined) => void): () => void {
  const take = (stamp: unknown): void => {
    const got = parseSessionStamp(stamp);
    // ⚠️ Свою рассылку отсекаем сами: канал не доставляет только объекту-
    // отправителю, а слушатель в этой же вкладке это другой объект
    if (got !== undefined && got.tab !== TAB_ID) fn(got.identity);
  };
  if (typeof BroadcastChannel !== "undefined") {
    const ch = new BroadcastChannel(SESSION_CHANGED_CHANNEL);
    ch.onmessage = (e: MessageEvent) => take(e.data);
    return () => ch.close();
  }
  const onStorage = (e: StorageEvent): void => {
    if (e.key === SESSION_CHANGED_CHANNEL) take(e.newValue);
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}
